'use client';

import { useState, useRef, useCallback } from 'react';

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript('');
      chunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        } 
      });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
      });
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
      
    } catch (err) {
      console.error('Recording error:', err);
      setError('マイクへのアクセスが拒否されました');
      setIsSupported(false);
    }
  }, []);

  const stopRecording = useCallback(async (): Promise<string> => {
    if (!mediaRecorderRef.current || !isRecording) return '';
    
    return new Promise<string>((resolve) => {
      const mediaRecorder = mediaRecorderRef.current!;
      
      mediaRecorder.onstop = async () => {
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
        
        const audioBlob = new Blob(chunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        console.log('Audio recorded:', audioBlob.size, 'bytes');
        
        if (audioBlob.size < 1000) {
          setError('録音データが短すぎます');
          setIsRecording(false);
          resolve('');
          return;
        }
        
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          
          const res = await fetch('/api/transcribe', {
            method: 'POST',
            body: formData,
          });
          
          const result = await res.json();
          
          if (result.success && result.data) {
            setTranscript(result.data.transcript);
            resolve(result.data.transcript);
          } else {
            setError(result.error || '文字起こしに失敗しました');
            resolve('');
          }
        } catch (err) {
          console.error('Transcription error:', err);
          setError('文字起こしに失敗しました');
          resolve('');
        } finally {
          setIsTranscribing(false);
        }
        
        setIsRecording(false);
      };
      
      mediaRecorder.stop();
    });
  }, [isRecording]);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
    chunksRef.current = [];
  }, []);

  return {
    isRecording,
    isTranscribing,
    transcript,
    error,
    isSupported,
    startRecording,
    stopRecording,
    reset,
    setTranscript,
  };
}
